import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi, type ImportPreview } from "@/lib/adminApi";
import { adminAuth } from "@/lib/adminAuth";
import { canManageQuestions } from "@/lib/roles";

export default function AdminQuestionsImport() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["admin-me"], queryFn: () => adminAuth.me(), retry: false });
  if (!me.isLoading && !canManageQuestions(me.data)) return <Navigate to="/admin" replace />;
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: () => adminApi.uploadImport(file!),
    onSuccess: (r) => { setPreview(r); setError(null); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? "Upload failed."),
  });
  const commit = useMutation({
    mutationFn: () => adminApi.commitImport(preview!.jobId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["questions"] });
      qc.invalidateQueries({ queryKey: ["categories"] });
      navigate("/admin/questions");
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? "Commit failed."),
  });

  return (
    <main className="mx-auto max-w-5xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-brand-ink">Bulk import questions</h1>
        <Link to="/admin/questions" className="text-sm text-brand hover:underline">← Back to list</Link>
      </div>

      {!preview && (
        <div className="card p-6">
          <p className="text-sm text-brand-ink/70">
            Upload an .xlsx that matches the template. Rows with an <code>external_id</code> matching
            an existing question will be <b>updated</b>; others are <b>inserted</b>. Unknown categories
            are auto-created on commit.
          </p>
          <p className="mt-2 text-sm">
            <a className="text-brand hover:underline" href={adminApi.importTemplateUrl()}>
              Download the Excel template
            </a>
          </p>

          <div className="mt-4">
            <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm" />
          </div>
          {error && <div className="mt-3 text-sm text-brand">{error}</div>}
          <div className="mt-4">
            <button className="btn-primary" disabled={!file || upload.isPending} onClick={() => upload.mutate()}>
              {upload.isPending ? "Uploading…" : "Preview import"}
            </button>
          </div>
        </div>
      )}

      {preview && (
        <>
          <div className="card mb-4 grid grid-cols-2 gap-4 p-6 sm:grid-cols-5">
            <Stat label="Total rows" value={preview.totalRows} />
            <Stat label="Insert" value={preview.insertCount} tone="emerald" />
            <Stat label="Update" value={preview.updateCount} tone="amber" />
            <Stat label="Unchanged" value={preview.unchangedCount} tone="gray" />
            <Stat label="Error" value={preview.errorCount} tone="brand" />
          </div>

          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-brand-light text-brand-ink">
                  <tr>
                    {["#", "Outcome", "External ID", "Category", "Difficulty", "Question", "Errors"].map((h) => (
                      <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.rowNumber} className="border-t border-brand-ink/10 align-top">
                      <td className="px-3 py-2 text-xs text-brand-ink/60">{r.rowNumber}</td>
                      <td className="px-3 py-2">
                        <span className={`rounded-pill px-2 py-0.5 text-xs
                          ${r.outcome === "Insert" ? "bg-emerald-100 text-emerald-800"
                            : r.outcome === "Update" ? "bg-amber-100 text-amber-800"
                            : r.outcome === "Unchanged" ? "bg-gray-100 text-gray-700"
                            : "bg-brand-light text-brand"}`}>
                          {r.outcome}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">{r.externalId ?? "—"}</td>
                      <td className="px-3 py-2 text-xs">{r.category ?? "—"}</td>
                      <td className="px-3 py-2 text-xs">{r.difficulty ?? "—"}</td>
                      <td className="max-w-md truncate px-3 py-2">{r.questionText ?? "—"}</td>
                      <td className="px-3 py-2 text-xs text-brand">
                        {r.errors.length > 0 ? (
                          <ul className="list-disc pl-4">{r.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                        ) : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {error && <div className="mt-3 text-sm text-brand">{error}</div>}

          <div className="mt-4 flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => { setPreview(null); setFile(null); setError(null); }}>
              Discard and start over
            </button>
            <button className="btn-primary"
              disabled={commit.isPending || preview.insertCount + preview.updateCount === 0}
              onClick={() => commit.mutate()}>
              {commit.isPending ? "Committing…"
                : `Commit ${preview.insertCount} inserts + ${preview.updateCount} updates`}
            </button>
          </div>
        </>
      )}
    </main>
  );
}

function Stat({ label, value, tone = "gray" }: { label: string; value: number; tone?: "gray" | "emerald" | "amber" | "brand" }) {
  const bg = tone === "emerald" ? "bg-emerald-50 text-emerald-800"
    : tone === "amber" ? "bg-amber-50 text-amber-800"
    : tone === "brand" ? "bg-brand-light text-brand"
    : "bg-gray-50 text-gray-800";
  return (
    <div className={`rounded-xl p-3 ${bg}`}>
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
    </div>
  );
}
